import { orgAPI } from "@/api/api";
import { useCreateCanvas } from "@/api/canvas";
import { useWorkspaces } from "@/api/workspaces";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { ATTEMPTS, BACKOFF_MS, sleep } from "./director/needs";
import { useOnboardingStage } from "./gate";
import { useOnboardingRecord, useOnboardingStatus } from "./status";
import { openTourPatch, readProgress, resumePatch } from "./tour/progress";
import {
  DEFAULT_TOUR,
  TOUR_CANVAS_DESCRIPTION,
  TOUR_CANVAS_NAME,
} from "./tours";
import type { Handoff } from "./types";

/** Where opening the tour stands, for whoever shows it. */
export type StartState = "idle" | "working" | "slow" | "failed";

export const START_SLOW_MS = 4_000;
export const START_TIMEOUT_MS = 25_000;

// One start at a time, whoever asked for it: the welcome dialog, the home
// card and the home slot's fallback all read the same state.
let startState: StartState = "idle";
const listeners = new Set<() => void>();

function setStartState(next: StartState) {
  if (next === startState) return;
  startState = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useStartState(): StartState {
  return useSyncExternalStore(subscribe, () => startState);
}

/** Put a failed start away without trying again. */
export function dismissStart() {
  if (startState === "failed") setStartState("idle");
}

/**
 * An archived or deleted canvas is no canvas: the tour starts a new one. A
 * request that merely failed says nothing about the canvas, and throws.
 */
async function canOpen(canvasId: string): Promise<boolean> {
  try {
    const canvas = await orgAPI.canvas.getById(canvasId);
    return !canvas.archived_at;
  } catch (err) {
    const code = (err as { response?: { status?: number } }).response?.status;
    if (code === 403 || code === 404 || code === 410) return false;
    throw err;
  }
}

async function retried<T>(run: () => Promise<T>): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    if (attempt > 0) await sleep(BACKOFF_MS[attempt - 1] ?? 0);
    try {
      return await run();
    } catch (err) {
      last = err;
    }
  }
  throw last;
}

/**
 * Open the walkthrough. The record moves to `in_progress` in the same patch
 * that names the tour's canvas, once that canvas exists: writing the status
 * on the click would unmount the welcome dialog mid-create, leaving the user
 * on a home that offers to finish a tour with no canvas behind it. It
 * remembers the canvas, so a second start picks the tour up there, at the
 * step it was left on, instead of leaving another canvas behind.
 *
 * Every request is tried three times; `state` says when it is taking long
 * and when it has failed, and `start` again is the retry.
 */
export function useStartTour(): {
  start: () => void;
  pending: boolean;
  state: StartState;
} {
  const navigate = useNavigate();
  const status = useOnboardingStatus();
  const { record } = useOnboardingRecord();
  const workspaces = useWorkspaces();
  const createCanvas = useCreateCanvas();
  const state = useStartState();

  const latest = useRef({
    navigate,
    status,
    record,
    workspaces,
    create: createCanvas.mutateAsync,
  });
  latest.current = {
    navigate,
    status,
    record,
    workspaces,
    create: createCanvas.mutateAsync,
  };

  const start = useCallback(() => {
    if (startState === "working" || startState === "slow") return;
    setStartState("working");
    let over = false;

    const open = (canvasId: string, handoff?: Handoff) => {
      latest.current.navigate(route(ROUTES.CANVAS.DETAIL, { canvasId }), {
        state: handoff ? { onboarding: handoff } : undefined,
      });
    };

    const run = async () => {
      const existing = readProgress(latest.current.record, DEFAULT_TOUR);
      if (
        existing &&
        !existing.done &&
        (await retried(() => canOpen(existing.canvasId)))
      ) {
        // Left through "Exit tour", or skipped: the same canvas, the stamped
        // step. Otherwise the record is already started and names this
        // canvas, and there is nothing to write.
        if (existing.exited || latest.current.record?.global !== "in_progress")
          await retried(async () => {
            if (!(await latest.current.status.patch(resumePatch(DEFAULT_TOUR))))
              throw new Error("tour not resumed");
          });
        if (!over) open(existing.canvasId);
        return;
      }

      // A click before the workspace list has loaded waits for it.
      const workspaceId = await retried(async () => {
        const now = latest.current.workspaces;
        const list = now.data ?? (await now.refetch()).data;
        const id = list?.find((ws) => ws.is_private)?.id;
        if (!id) throw new Error("no private workspace");
        return id;
      });
      const canvas = await retried(() =>
        latest.current.create({
          workspaceId,
          name: TOUR_CANVAS_NAME,
          description: TOUR_CANVAS_DESCRIPTION,
        }),
      );
      // One write, before the navigate: the patch shows in the cache at once,
      // so the canvas page reads a record that is started and already names
      // the canvas it is opening.
      void latest.current.status.patch({
        ...openTourPatch(latest.current.record, DEFAULT_TOUR, canvas.id),
        global: "in_progress",
      });
      if (!over) open(canvas.id, { tour: DEFAULT_TOUR });
    };

    const slow = window.setTimeout(() => {
      if (startState === "working") setStartState("slow");
    }, START_SLOW_MS);
    const timeout = window.setTimeout(() => {
      over = true;
      setStartState("failed");
    }, START_TIMEOUT_MS);
    run()
      .then(
        () => {
          if (!over) setStartState("idle");
        },
        () => {
          if (!over) setStartState("failed");
        },
      )
      .finally(() => {
        over = true;
        window.clearTimeout(slow);
        window.clearTimeout(timeout);
      });
  }, []);

  return { start, pending: state === "working" || state === "slow", state };
}

export interface OnboardingEntry {
  /** The user started or skipped the walkthrough and has not finished it. */
  visible: boolean;
  /** The walkthrough is still ahead of the user: not started, started or skipped. */
  offered: boolean;
  /** The canvas the walkthrough runs on, once one exists and it is not done. */
  canvasId?: string;
  start: () => void;
  dismiss: () => void;
  pending: boolean;
}

/**
 * The "Finish onboarding" offer on the home browser. The card is production's;
 * whether it applies, and what its two actions do, are onboarding's.
 */
export function useOnboardingEntry(): OnboardingEntry {
  const stage = useOnboardingStage();
  const status = useOnboardingStatus();
  const { record } = useOnboardingRecord();
  const { start, pending } = useStartTour();
  const progress = readProgress(record, DEFAULT_TOUR);
  const dismiss = useCallback(() => {
    void status.finish();
  }, [status]);
  const visible = stage === "started" || stage === "skipped";
  return {
    visible,
    offered: visible || stage === "new",
    canvasId: progress && !progress.done ? progress.canvasId : undefined,
    start,
    dismiss,
    pending,
  };
}
